"use client"

import { createContext, useContext, useState, useEffect } from "react"
import { useLocation, useNavigate } from "react-router-dom"

// Create context
const BreadcrumbContext = createContext()

/**
 * Map of route paths to human-readable names
 * Add your routes here as your application grows
 */
const routeNameMap = {
  "": "Dashboard",
  dashboard: "Dashboard",
  View_Staff: "View Staff",
  Add_Staff: "Add Staff",
  edit_staff: "Edit Staff",
  staff_details: "Staff Details",
  settings: "Settings",
  profile: "Profile",
  // Add more routes as needed
}

/**
 * Breadcrumb Provider Component
 * Wraps your application to provide breadcrumb functionality
 */
export function BreadcrumbProvider({ children }) {
  const [breadcrumbs, setBreadcrumbs] = useState([])
  const location = useLocation()
  const navigate = useNavigate()

  // Update breadcrumbs when location changes
  useEffect(() => {
    const pathSegments = location.pathname.split("/").filter((segment) => segment)

    // Generate breadcrumbs from path segments
    const breadcrumbItems = pathSegments.map((segment, index) => {
      // Create path up to this segment
      const path = "/" + pathSegments.slice(0, index + 1).join("/")

      // Check if we have a custom name for this route in our map
      // If not, format the segment (convert kebab-case or snake_case to Title Case)
      let label

      // Check if this is an ID segment (contains only numbers)
      if (/^\d+$/.test(segment)) {
        // This is likely an ID, use the previous segment's singular form
        const prevSegment = pathSegments[index - 1]
        if (prevSegment) {
          // Convert plural to singular if possible (simple s removal)
          const singularForm = prevSegment.endsWith("s") ? prevSegment.slice(0, -1) : prevSegment
          label = `${formatRouteSegment(singularForm)} Details`
        } else {
          label = `ID: ${segment}`
        }
      } else {
        // Use the route map or format the segment
        label = routeNameMap[segment] || formatRouteSegment(segment)
      }

      return { label, path }
    })

    // Always include home/dashboard as the first breadcrumb
    const updatedBreadcrumbs = [{ label: "Dashboard", path: "/" }, ...breadcrumbItems]

    setBreadcrumbs(updatedBreadcrumbs)
  }, [location])

  // Navigate to the selected breadcrumb
  const navigateTo = (path, index) => {
    if (index < breadcrumbs.length - 1) {
      navigate(path)
    }
  }

  // Set custom breadcrumbs manually (useful for dynamic pages)
  const setCustomBreadcrumbs = (customBreadcrumbs) => {
    setBreadcrumbs(customBreadcrumbs)
  }

  return (
    <BreadcrumbContext.Provider value={{ breadcrumbs, navigateTo, setCustomBreadcrumbs }}>
      {children}
    </BreadcrumbContext.Provider>
  )
}

// Helper function to format route segments into readable labels
function formatRouteSegment(segment) {
  return segment
    .replace(/[-_]/g, " ")
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase())
}

// Custom hook to use breadcrumb context
export function useBreadcrumb() {
  const context = useContext(BreadcrumbContext)
  if (!context) {
    throw new Error("useBreadcrumb must be used within a BreadcrumbProvider")
  }
  return context
}
